import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useCallback, useEffect } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
export const Scratchpad = ({ isOpen, onClose }) => {
    const [content, setContent] = useLocalStorage('multiwise_scratchpad_content', '');
    const editorRef = useRef(null);
    // Initialize editor content when modal opens
    useEffect(() => {
        if (isOpen && editorRef.current) {
            if (!content.trim()) {
                editorRef.current.innerHTML = '<p>Start writing your notes and ideas here...</p>';
            }
            else {
                editorRef.current.innerHTML = content;
            }
            // Focus the editor
            setTimeout(() => {
                if (editorRef.current) {
                    editorRef.current.focus();
                    // Place cursor at the end
                    const range = document.createRange();
                    const sel = window.getSelection();
                    range.selectNodeContents(editorRef.current);
                    range.collapse(false);
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                }
            }, 100);
        }
    }, [isOpen]);
    const execCommand = useCallback((command, value) => {
        if (!editorRef.current)
            return;
        // Ensure editor is focused
        editorRef.current.focus();
        try {
            let success = false;
            // Special handling for different commands
            if (command === 'formatBlock' && value) {
                // For headings and paragraphs, use the full HTML tag
                const tagMap = {
                    'h1': '<h1>',
                    'h2': '<h2>',
                    'h3': '<h3>',
                    'p': '<p>'
                };
                const htmlTag = tagMap[value] || `<${value}>`;
                success = document.execCommand('formatBlock', false, htmlTag);
            }
            else if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
                // For lists, just execute the command
                success = document.execCommand(command, false);
            }
            else {
                // For other commands (bold, italic, underline)
                success = document.execCommand(command, false, value);
            }
            console.log(`Command ${command} ${value || ''} executed: ${success}`);
            // Save content after command
            setTimeout(() => {
                if (editorRef.current) {
                    setContent(editorRef.current.innerHTML);
                }
            }, 50);
        }
        catch (error) {
            console.warn('Command failed:', command, error);
        }
    }, [setContent]);
    const handleContentChange = useCallback(() => {
        if (editorRef.current) {
            setContent(editorRef.current.innerHTML);
        }
    }, [setContent]);
    const clearContent = useCallback(() => {
        if (editorRef.current) {
            editorRef.current.innerHTML = '<p>Start writing your notes and ideas here...</p>';
            setContent('');
            editorRef.current.focus();
        }
    }, [setContent]);
    // Handle keyboard shortcuts
    const handleKeyDown = useCallback((e) => {
        if (e.ctrlKey || e.metaKey) {
            switch (e.key) {
                case 'b':
                    e.preventDefault();
                    execCommand('bold');
                    break;
                case 'i':
                    e.preventDefault();
                    execCommand('italic');
                    break;
                case 'u':
                    e.preventDefault();
                    execCommand('underline');
                    break;
            }
        }
    }, [execCommand]);
    if (!isOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
        .scratchpad-editor h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        .scratchpad-editor h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        .scratchpad-editor h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 1em 0;
        }
        .scratchpad-editor p {
          margin: 1em 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .scratchpad-editor ul {
          margin: 1em 0;
          padding-left: 2em;
          list-style-type: disc;
        }
        .scratchpad-editor ol {
          margin: 1em 0;
          padding-left: 2em;
          list-style-type: decimal;
        }
        .scratchpad-editor li {
          margin: 0.5em 0;
          display: list-item;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .scratchpad-editor * {
          max-width: 100%;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      ` }), _jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col m-4", children: [_jsxs("div", { className: "flex items-center justify-between p-4 border-b border-gray-200", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("svg", { className: "w-5 h-5 text-purple-600", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }), _jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "Personal Scratchpad" })] }), _jsx("div", { className: "flex items-center space-x-2", children: _jsx("button", { onClick: onClose, className: "p-1 text-gray-400 hover:text-gray-600 rounded transition-colors", title: "Close scratchpad", children: _jsx("svg", { className: "w-6 h-6", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) }) })] }), _jsxs("div", { className: "flex items-center space-x-1 p-3 border-b border-gray-200 bg-gray-50", children: [_jsx("button", { onClick: () => execCommand('formatBlock', 'h1'), className: "px-3 py-1.5 text-sm font-bold hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Heading 1", children: "H1" }), _jsx("button", { onClick: () => execCommand('formatBlock', 'h2'), className: "px-3 py-1.5 text-sm font-semibold hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Heading 2", children: "H2" }), _jsx("button", { onClick: () => execCommand('formatBlock', 'h3'), className: "px-3 py-1.5 text-sm font-medium hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Heading 3", children: "H3" }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-2" }), _jsx("button", { onClick: () => execCommand('bold'), className: "p-2 hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Bold (Ctrl+B)", children: _jsxs("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" })] }) }), _jsx("button", { onClick: () => execCommand('italic'), className: "p-2 hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Italic (Ctrl+I)", children: _jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M10 4l4 16M6 8h12M4 16h12" }) }) }), _jsx("button", { onClick: () => execCommand('underline'), className: "p-2 hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Underline (Ctrl+U)", children: _jsx("span", { className: "text-sm font-medium underline", children: "U" }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-2" }), _jsx("button", { onClick: () => execCommand('insertUnorderedList'), className: "p-2 hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Bullet List", children: _jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 6h16M4 12h16M4 18h16" }) }) }), _jsx("button", { onClick: () => execCommand('insertOrderedList'), className: "p-2 hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Numbered List", children: _jsxs("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M3 4h18M3 12h18M3 20h18" }), _jsx("circle", { cx: "2", cy: "4", r: "1", fill: "currentColor" }), _jsx("circle", { cx: "2", cy: "12", r: "1", fill: "currentColor" }), _jsx("circle", { cx: "2", cy: "20", r: "1", fill: "currentColor" })] }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-2" }), _jsx("button", { onClick: () => execCommand('formatBlock', 'p'), className: "px-3 py-1.5 text-sm hover:bg-gray-200 rounded transition-colors border border-gray-300", title: "Normal Text", children: "P" })] }), _jsx("div", { className: "flex-1 p-6 overflow-hidden", children: _jsx("div", { ref: editorRef, contentEditable: true, onInput: handleContentChange, onKeyDown: handleKeyDown, suppressContentEditableWarning: true, className: "scratchpad-editor w-full h-full min-h-[500px] p-4 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white overflow-y-auto", style: {
                                    lineHeight: '1.6',
                                    fontSize: '16px',
                                    fontFamily: 'system-ui, -apple-system, sans-serif',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    maxWidth: '100%',
                                } }) }), _jsx("div", { className: "p-3 border-t border-gray-200 bg-gray-50", children: _jsxs("div", { className: "flex items-center justify-between text-xs text-gray-500", children: [_jsx("span", { children: "Your notes are automatically saved locally" }), _jsx("span", { children: "Use Ctrl+B for bold, Ctrl+I for italic, Ctrl+U for underline" })] }) })] }) })] }));
};
